import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, Inject, PLATFORM_ID } from '@angular/core';
import { forkJoin } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from "@angular/forms";
import { PaginatorModule } from 'primeng/paginator';

import { AuthService } from '../../../../services/auth.service';
import { PageTitleService } from '../../../../services/pagetitle.service';
import { TimesheetService } from '../../../../services/timesheet.service';
import { EncryptionService } from '../../../../services/encryption.service';
import { Base64UrlService } from '../../../../services/base64-url.service';
import { AdminService } from '../../../../services/admin.service';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-associate-approve-all',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule, PaginatorModule],
  providers: [Base64UrlService],
  templateUrl: './associate-approve-all.component.html',
  styleUrl: './associate-approve-all.component.scss'
})

export class AssociateApproveAllComponent{

  lang: any;
  associatesList: any[] = [];
  timeSheetData: any;
  monthDropdown: any[] = [];
  userCode: any;
  // These hold filtered data
  visibleData: any[] = [];
  totalCount: number = 0;
  dropDowndate: any;
  sectionId: any = '-1';

  // Manual pagination
  currentPage: number = 1;
  itemsPerPage: number = 20;
  searchText: string = '';
  filteredData: any[] = [];
  expandedRows: { [key: string]: boolean } = {};

  isButtonDisabled: boolean = false;
  dashboard_msg: string | null = null;
  msgType: any;
  customerID: any;
  thread_msg: any = null;
  editingRow: number | null = null;
  selectAll = false;
  archive: boolean = false;
  isLoading: boolean = true;
  hasCheckableData: boolean = false;
  adminLevel: number = 0;
  companyName: any;

  isSaveDisabled: boolean = true;

  private updateSaveDisabled(): void {
    const hasChecked = this.associatesList.some((item: any) => item.markedForSubmit);
    this.isSaveDisabled = this.archive || !hasChecked;
  }

  private getApprovalStatus(item: any): number {
    return Number(item.currentStateID ?? item.CurrentStateID ?? item.status);
  }

  private getApprovalSortOrder(item: any): number {
    const status = this.getApprovalStatus(item);

    if (status === 20) return 0;
    if (status === 30) return 1;
    if (status < 20) return 2;
    if (status === 40) return 3;

    return 4;
  }

  private compareAssociateApprovalOrder(a: any, b: any): number {
    const approvalOrderDiff = this.getApprovalSortOrder(a) - this.getApprovalSortOrder(b);
    if (approvalOrderDiff !== 0) return approvalOrderDiff;

    const officeCodeDiff = (a.customerOfficeCode ?? '').localeCompare(b.customerOfficeCode ?? '', 'en', { sensitivity: 'variant' });
    if (officeCodeDiff !== 0) return officeCodeDiff;

    return (a.userCode ?? '').localeCompare(b.userCode ?? '', 'en', { sensitivity: 'variant' });
  }

  constructor(private auth: AuthService, private translate: TranslateService, private ts: TimesheetService, private en: EncryptionService, private pageTitle: PageTitleService, private http: HttpClient, @Inject(PLATFORM_ID) private platformId: Object, private ams: AdminService) {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {

      this.lang = (localStorage.getItem('site_lang') ? localStorage.getItem('site_lang') : 'jp');
      // console.log(this.lang)
      translate.use(this.lang);
    }
  }

  ngOnInit() {
    console.log('🚀 associate-approve-all component initialized');
    console.time('⏱️ Total component load');
    this.userCode = this.auth.getCode();
    this.adminLevel = this.auth.getAdminLevel() || 0;
    this.companyName = this.auth.getCompanyName();
    this.pageTitle.setTitle('menu.approveall');

    if (isPlatformBrowser(this.platformId)) {
      this.loadAllData();
    }
  }

  loadAllData() {
    this.isLoading = true;
    
    // デフォルト値を計算
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const day = today.getDate();
    const defaultDateFormat = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    // まずcustomerIDを取得してから、他のAPIを並列実行
    console.time('⏱️ API: getCustomers');
    this.ams.getCustomers().subscribe({
      next: (customersResult: any) => {
        console.timeEnd('⏱️ API: getCustomers');
        
        // 顧客IDを設定
        if (!customersResult.status || !customersResult.data?.length) {
          this.isLoading = false;
          return;
        }
        this.customerID = customersResult.data[0].customerID;
        
        // 残り2つのAPIを並列実行
        console.time('⏱️ Parallel: getAproveAllDateList + getAssociatesBatchList');
        console.time('⏱️ API: getAproveAllDateList');
        console.time('⏱️ API: getAssociatesBatchList');
        
        forkJoin({
          dateList: this.ts.getAproveAllDateList(),
          batchList: this.ts.getAssociatesBatchList(defaultDateFormat, this.customerID)
        }).subscribe({
          next: (results: any) => {
            console.timeEnd('⏱️ API: getAproveAllDateList');
            console.timeEnd('⏱️ API: getAssociatesBatchList');
            console.timeEnd('⏱️ Parallel: getAproveAllDateList + getAssociatesBatchList');
            console.timeEnd('⏱️ Total component load');
            
            // 日付リストの処理
            this.monthDropdown = results.dateList.data;
            this.dropDowndate = `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`;
            
            // バッチリストの処理
            this.processAssociatesData(results.batchList);
          },
          error: (err: any) => {
            console.timeEnd('⏱️ API: getAproveAllDateList');
            console.timeEnd('⏱️ API: getAssociatesBatchList');
            console.timeEnd('⏱️ Parallel: getAproveAllDateList + getAssociatesBatchList');
            console.timeEnd('⏱️ Total component load');
            console.error('❌ Phase 2 API Error:', err);
            this.isLoading = false;
            
            if (err.status === 401) {
              this.auth.logout();
            }
          }
        });
      },
      error: (err: any) => {
        console.timeEnd('⏱️ API: getCustomers');
        console.timeEnd('⏱️ Total component load');
        console.error('❌ getCustomers Error:', err);
        this.isLoading = false;
        
        if (err.status === 401) {
          this.auth.logout();
          return;
        }
      }
    });
  }

  processAssociatesData(res: any) {
    console.log('📊 Raw batch data - lineItems count:', res.data?.lineItems?.length);
    console.time('⏱️ Data Processing');
    // console.log(res);
    this.archive = res.data.isArchived;
    // this.associatesList = this.applyEncryption(res.data);
    // this.associatesList = res.data.lineItems;
    const sections = res.data.lineItems || [];
    // console.log(sections);
    // 最適化: 特別な日付を定数化し、処理を効率化
    const EMPTY_DATE = '1972-01-12T00:00:00';
    
    this.associatesList = [...sections].sort((a: any, b: any) =>
      (a.customerOfficeCode ?? '').localeCompare(b.customerOfficeCode ?? '', 'en', { sensitivity: 'variant' })
    ).flatMap((section: any) => {
      const customerOfficeId = section.customerOfficeId;
      const customerOfficeName = section.customerOfficeName;
      const customerOfficeCode = section.customerOfficeCode ?? '';
      const customerName = section.customerName ?? '';
      const markedForSubmit = section.markedForSubmit ?? false;
      const lineItems = section.lineItems || [];
      const groupSize = lineItems.length;
      
      return [...lineItems].sort((a: any, b: any) =>
        (a.userCode ?? '').localeCompare(b.userCode ?? '', 'en', { sensitivity: 'variant' })
      ).map((asso: any, index: number) => {
        const startTimeOrg = asso.startTime;
        const endTimeOrg = asso.endTime;
        const isEmptyStart = startTimeOrg === EMPTY_DATE;
        const isEmptyEnd = endTimeOrg === EMPTY_DATE;
        
        return {
          ...asso,
          customerOfficeId,
          customerOfficeName,
          customerOfficeCode,
          customerName,
          startTime: isEmptyStart ? '' : this.toTimeString(startTimeOrg),
          startTimeOrg,
          endTime: isEmptyEnd ? '' : this.getTimeDiffInMinutes(startTimeOrg, endTimeOrg),
          endTimeOrg,
          markedForSubmit,
          _rowIndex: index,
          _groupSize: groupSize,
          _dayInfo: this.getDayWeekKey(asso.dayDate),
          _statusKey: this.getStatus(asso.status).statusKey,
          _noteKey: this.getNote(asso.noteId).noteKey,
          _expensesKey: this.getExpenses(asso.expenseId).expensesKey,
        };
      });
    }).sort((a: any, b: any) => this.compareAssociateApprovalOrder(a, b));
    
    console.log(this.associatesList)
    console.timeEnd('⏱️ Data Processing');
    console.log('📊 Processed associates count:', this.associatesList.length);
    // this.totalCount = this.associatesList?.length;
    this.applyFilters();
    this.updateSaveDisabled();
    this.isLoading = false;
    console.log('✅ Batch list ready');
  }

  getTimeDiffInMinutes(start: string, end: string) {    
    const startTime = new Date(start).getTime();
    const endTime = new Date(end).getTime();
    const diffMs = endTime - startTime;
    const diffMin = Math.floor(diffMs / (1000 * 60));    // convert to minutes
    let rt = this.addMinutesWithOverflow(start, diffMin).split('T')[1].split(':');
    let finalEndTime = `${parseInt(rt[0], 10)}:${rt[1]}`;
    return finalEndTime;
  }
  
  addMinutesWithOverflow(dateStr: string, minutes: number): string {
    const base = new Date(dateStr);
  
    // Add minutes normally
    const updated = new Date(base.getTime() + minutes * 60000);
  
    // Calculate total minutes since start of the day
    const diffMs = updated.getTime() - new Date(base.toDateString()).getTime();
    const totalMinutes = diffMs / 60000;
  
    const hours = Math.floor(totalMinutes / 60);
    const mins = Math.floor(totalMinutes % 60);
  
    // Format back as overflow time
    const hh = hours.toString().padStart(2, '0');
    const mm = mins.toString().padStart(2, '0');
  
    const datePart = dateStr.split("T")[0];
  
    return `${datePart}T${hh}:${mm}:00`;
  }

  toTimeString(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const hours = date.getHours().toString();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }
  getDayWeekKey(dateString: string): { day: string, weekdayKey: string, shortweekday: string } {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const weekdayIndex = date.getDay();

    const weekdayKeys = [
      'WEEKDAYS.SUNDAY',
      'WEEKDAYS.MONDAY',
      'WEEKDAYS.TUESDAY',
      'WEEKDAYS.WEDNESDAY',
      'WEEKDAYS.THURSDAY',
      'WEEKDAYS.FRIDAY',
      'WEEKDAYS.SATURDAY'
    ];

    const weekdayKeysShort = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

    return { day, weekdayKey: weekdayKeys[weekdayIndex], shortweekday: weekdayKeysShort[weekdayIndex] };
  }
  getAssociatesList() {
    const [day, month, year] = this.dropDowndate.split('-');
    let dateFormat = `${year}-${month}-${day}`;
    
    console.time('⏱️ getAssociatesBatchList API');
    this.ts.getAssociatesBatchList(dateFormat, this.customerID)
      .subscribe({
        next: (res: any) => {
          console.timeEnd('⏱️ getAssociatesBatchList API');
          this.processAssociatesData(res);
        },
        error: (err: any) => {
          console.timeEnd('⏱️ getAssociatesBatchList API');
          if (err.status === 401) {
            this.auth.logout();
            return;
          }
        }
      });
  }

  fncCng(event: any) {    
    this.getAssociatesList();
  }

  chkSubmitCnt() {
    const checked = this.associatesList.filter((item: any) => item.markedForSubmit).length;
    this.selectAll = (this.associatesList.length > 0 && checked === this.associatesList.length);
    this.updateSaveDisabled();
  }

  onRejectClick(item: any): void {
    if (item.status === 30 || item.status === 20) {      
      // console.log(this.lang + " - - " + );item.timesheetLineItemId
      this.ts.getEtcThread(item.timesheetLineItemId, this.lang )
      .subscribe({
        next: (res: any) => {
            this.thread_msg = (res as any[]).map((msg: any) => ({
              ...msg,
              _modifyDate: this.formatDateTime(msg.modifyDate)
            }));
          },
          error: (err: any) => {
            console.log(err)
            if(err.status == 401){
              this.auth.logout()
			        return
            }
          }
      })

    }
  }

  threadClose(): void {
    this.thread_msg = null;
  }

  formatDateTime(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day} : ${hours}:${minutes}`;
  }

  getStatus(status: number): { statusKey: string } {
    let statusIndex = 0;
    if (status == 40) {
      statusIndex = 4;
    } else if (status == 30) {
      statusIndex = 3;
    } else if (status == 20) {
      statusIndex = 1;
    } else {
      statusIndex = 0;
    }
    const statusKeys = [
      "SHORTSTATUS.NOTSUBMIT",
      "SHORTSTATUS.SUBMIT",
      "SHORTSTATUS.FIX",
      "SHORTSTATUS.REJECT",
      "SHORTSTATUS.APPROVE"
    ];
    return {statusKey: statusKeys[statusIndex]}
  }

  private getDateFromEvent(event: any): string {
    if (!event) return '2025-07-01';
    const target = event.target as HTMLSelectElement;
    return this.getDate(target.value);
  }

  getDate(dateStr: string): string {
    const [day, month, year] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const yyyy = date.getFullYear();
    const mm = (date.getMonth() + 1).toString().padStart(2, '0');
    const dd = date.getDate().toString().padStart(2, '0');

    return `${yyyy}-${mm}-${dd}`;
  }

  countSubmitMarked() {
    return this.associatesList.filter((x: any) => x.markedForSubmit).length;
  }
  
  onSave(): void {
    const startTimeRegex = /^(?:0[1-9]|1\d|2[0-3]|[1-9]):[0-5]\d$/;
    let hasError = false;
    let cntApprove = 0;
    cntApprove = this.countSubmitMarked();
    
    // console.log(cntApprove);
    if (cntApprove == 0) {
      hasError = true;
    }
    // stop if any error found
    if (hasError) {
      // console.log("Validation failed. Please correct errors before submitting.");
      this.dashboard_msg = `${this.translate.instant('validationApproverErrorMessage')}`;
      this.msgType = "danger";

      alert(this.dashboard_msg)
      return;
    } 
    
    // Prepare payload
    // First filter the array based on the checkbox checked then map the filtrered array
    const items = this.associatesList.filter((item: any) => item.markedForSubmit).map((item: any) => ({
      timesheetLineItemId: item.timesheetLineItemId ?? 0,
      status: item.markedForSubmit ? 40 : item.status, 
      etcRemarks: item.etcRemarks ?? '',
      rowVersion: item.rowVersion ?? null,
    }));

    const payload = {
      timesheetId: 0,
      items,
    };

    // console.log(payload);
    // return;
    this.ts.approveRejectTimeSheet(payload)
      .subscribe({
        next: (res: any) => {
          if (res.status) {
            this.dashboard_msg = res.message; //"dataSaveSuccesMessage";
            this.msgType = "success";
            this.getAssociatesList();
            this.isButtonDisabled = false;
            this.selectAll = false;
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            this.dashboard_msg = res.message; //"dataSaveErrorMessage";
            this.msgType = "danger";
            this.getAssociatesList();
            this.isButtonDisabled = false;
            this.selectAll = false;
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        },
        error: (err: any) => {
          console.log(err);
          if (err.status == 401) { 
            this.auth.logout();
			      return
          } else {
            this.dashboard_msg = "dataSaveErrorMessage";
            this.msgType = "danger";
            this.getAssociatesList();
            this.isButtonDisabled = false;
            this.selectAll = false;
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }
    })
  }

  editReject(index: number) {
    // console.log(event);
    // const input = event.target as HTMLInputElement;
    // const isChecked = input.checked;
    // this.editingRow = index;
    
    if (this.associatesList[index].markedForReject) {      
      this.editingRow = index;
      this.archive = true;
    } else {
      this.editingRow = null;
      this.archive = false;  
    }
    this.updateSaveDisabled();
  }

  cancelReject(index: number) {
    // console.log("cancel reject");
    this.associatesList[index].markedForReject = false;
    this.editingRow = null;
    this.archive = false;
    this.selectAll = false;
    this.expandedRows[index.toString()] = false;
    // this.getAssociatesList(newFormat);
    this.getAssociatesList();
  }

  saveReject(index: number) {
    // console.log("save reject");
    this.editingRow = null;
    const item = this.associatesList[index];
        

    // Prepare single row payload
    const items = [{
      timesheetLineItemId: item.timesheetLineItemId ?? 0,
      // dayDate: item.dayDate ?? '',
      // startTime: this.toIsoString(item.startTime, item.startTimeOrg),
      // endTime: this.toIsoString(item.endTime, item.endTimeOrg) ?? '',
      // midnightBreakMinutes: item.midnightBreakMinutes ?? 0,
      // breakMinutes: item.breakMinutes ?? 0,
      // noteId: item.noteId ?? 0,
      // expenseId: item.expenseId ?? 0,
      status: 30,
      // cost: item.cost ?? 0,
      // whf: item.whf ?? false,
      etcRemarks: item.etcRemarks ?? '',
      rowVersion: item.rowVersion ?? null
    }];
// timesheetId: this.timesheetId,
    const payload = {      
      items
    };

    // console.log(payload);
    // return false;

    this.ts.approveRejectTimeSheet(payload)
      .subscribe({
        next: (res: any) => {
          if (res.status) {
            this.dashboard_msg = res.message; //"dataSaveSuccesMessage";
            this.msgType = "success";
            this.getAssociatesList();
            this.isButtonDisabled = false;
            this.archive = false;
            this.selectAll = false;
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            this.dashboard_msg = res.message;"dataSaveErrorMessage";
            this.msgType = "danger";
            this.getAssociatesList();
            this.isButtonDisabled = false;
            this.archive = false;
            this.selectAll = false;
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        },
        error: (err: any) => {
          console.log(err);
          if (err.status == 401) { 
            this.auth.logout();
			      return
          } else if (err.status === 409) {
            this.dashboard_msg = "dataConflictErrorMessage";
            this.msgType = "danger";
            this.ts.invalidateTimesheetDataCache();
            this.getAssociatesList();
            this.isButtonDisabled = false;
            this.archive = false;
            this.selectAll = false;
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            this.dashboard_msg = "dataSaveErrorMessage";
            this.msgType = "danger";
            this.getAssociatesList();
            this.isButtonDisabled = false;
            this.archive = false;
            this.selectAll = false;
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }
    })
  }

  applyFilterGlobal($event: Event) {
    const input = $event.target as HTMLInputElement;
    this.searchText = input.value;
    this.applyFilters();
  }

  // Manual pagination and filtering
  onSearchFilter(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchText = input.value;
    this.applyFilters();
  }

  applyFilters() {
    const searchLower = this.searchText.toLowerCase();
    
    if (!searchLower) {
      this.filteredData = this.associatesList;
    } else {
      this.filteredData = this.associatesList.filter((item: any) => {
        return (
          item.staffName?.toLowerCase().includes(searchLower) ||
          item.customerOfficeName?.toLowerCase().includes(searchLower)
        );
      });
    }
    
    // チェック可能なデータがあるかを判定（status=20のデータのみチェック可能）
    this.hasCheckableData = this.filteredData.some((item: any) => item.status === 20);
    
    this.currentPage = 1;
    this.updatePaginatedData();
  }

  updatePaginatedData() {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.visibleData = this.filteredData.slice(startIndex, endIndex);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredData.length / this.itemsPerPage);
  }

  get paginationInfo(): string {
    if (this.filteredData.length === 0) return '0 - 0 / 0';
    const start = (this.currentPage - 1) * this.itemsPerPage + 1;
    const end = Math.min(this.currentPage * this.itemsPerPage, this.filteredData.length);
    return `${start} - ${end} / ${this.filteredData.length}`;
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePaginatedData();
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePaginatedData();
    }
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePaginatedData();
    }
  }

  // PrimeNG Paginator event handler
  onPageChange(event: any) {
    this.currentPage = event.page + 1; // PrimeNG uses 0-based index
    this.itemsPerPage = event.rows;
    this.updatePaginatedData();
  }

  toggleRow(index: string) {
    const opening = !this.expandedRows[index];
    if (opening) {
      // 他の開いている行を全て閉じる（編集中の行はキャンセル扱い）
      Object.keys(this.expandedRows).forEach(key => {
        if (this.expandedRows[key] && key !== index) {
          if (this.editingRow === Number(key)) {
            this.cancelReject(Number(key));
          } else {
            this.expandedRows[key] = false;
          }
        }
      });
    } else {
      // 閉じるとき：編集モード中ならキャンセルと同じ動作
      if (this.editingRow === Number(index)) {
        this.cancelReject(Number(index));
        return;
      }
    }
    this.expandedRows[index] = opening;
  }

  // Update filteredData whenever filter or pagination changes
  onFilter(event: any) {
    this.updateVisibleData();
  }

  onPage(event: any) {
    this.updateVisibleData(event);
  }

  updateVisibleData(event?: any) {
    // This method is no longer needed with manual pagination
    // Keep for compatibility but redirect to updatePaginatedData
    this.updatePaginatedData();
  }

  toggleAllApprovals(event: Event) {
    const input = event.target as HTMLInputElement;
    const isChecked = input.checked;

    this.selectAll = isChecked; // update header state

  // Update all rows
  this.associatesList.forEach((item: any) => {
    if(item.status == 20)
    item.markedForSubmit = isChecked;
  });

    this.updateSaveDisabled();
  }

  getNote(note: number): { noteKey: string } {
    let statusIndex = 0;
    if (note == 10) {
      statusIndex = 0;
    } else if (note == 20) {
      statusIndex = 1;
    } else if (note == 40) {
      statusIndex = 2;
    } else if (note == 50) {
      statusIndex = 3;
    } else {
      statusIndex = 4;
    }
    const noteKeys = [
      "",
      "NOTEDD.PaidLeave",
      "NOTEDD.Tardiness",
      "NOTEDD.Absence",
      "NOTEDD.SeeComment"
    ];
    return {noteKey: noteKeys[statusIndex]}
  }
  
  getExpenses(expenses: number): { expensesKey: string } {
    let statusIndex = 0;
    if (expenses == 0) {
      statusIndex = 0;
    } else if (expenses == 1) {
      statusIndex = 1;
    } else if (expenses == 2) {
      statusIndex = 2;
    }
    const expensesKeys = [
      "",
      "ExpenseDD.AdvancePayment",
      "ExpenseDD.Transportation"
    ];
    return {expensesKey: expensesKeys[statusIndex]}
  }

  getShortText(text: string, limit: number = 30): string {
    return text && text.length > limit ? text.substring(0, limit) + '...' : text;
  }

  // filterStaff(event: any) {
  //   const query = event.target.value.toLowerCase();

  //   this.dt2.filter(
  //     query,
  //     'lineItems',
  //     'custom'
  //   );
  // }
  
  applyEncryption(dt: any) {
    dt.forEach((elm: any, ind: any) => {
      dt[ind].timesheetID = Base64UrlService.encode(this.en.encryptionAES(elm.timesheetID.toString()))
      dt[ind].userId = Base64UrlService.encode(this.en.encryptionAES(elm.userId.toString()))
    });
    return dt;
  }

  // addCustomFilter() {
  //   this.dt2.filterConstraints['custom'] = (value: any, filter: string): boolean => {
  //     if (!filter) return true;
  //     if (!value) return false;

  //     // value = lineItems array
  //     return value.some((x: any) =>
  //       (x.staffName ?? '').toLowerCase().includes(filter.toLowerCase())
  //     );
  //   };
  // }

  ngAfterViewInit(): void {
    // this.addCustomFilter();
    if (isPlatformBrowser(this.platformId)) {
        setTimeout(() => {
          ($('#ass_approve_table') as any).DataTable({
              ordering: false,
              paging: true,
              searching: true,
              info: true,
              //scrollX: true,
              "language": {
                "search": "Search for Name:"
              },
              columnDefs: [
                  {targets: [0, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13,14], searchable: false}
              ],
          });
        });
        }
}

ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      const table = ($('#ass_approve_table') as any).DataTable();
      if (table) {
        table.destroy(true);
      }
    }
  }


}
